import { CoachingFormData, READINESS_OPTIONS } from './types';

interface Props {
  data: CoachingFormData;
  onEdit: (step: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string;
  submitSuccess: boolean;
  hasScheduled: boolean;
}

function SummaryBlock({
  title,
  stepIndex,
  onEdit,
  children,
}: {
  title: string;
  stepIndex: number;
  onEdit: (step: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-[#F5F1E8] rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-widest text-[#C9A96E]">{title}</h4>
        <button
          type="button"
          onClick={() => onEdit(stepIndex)}
          className="text-xs text-[#C9A96E] hover:text-[#B89858] font-medium cursor-pointer whitespace-nowrap"
        >
          Edit
        </button>
      </div>
      <div className="text-sm text-[#1E2D5E] space-y-2">{children}</div>
    </div>
  );
}

function TagList({ items }: { items: string[] }) {
  if (!items.length) return <span className="text-[#1E2D5E]/40 italic">None selected</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="px-2.5 py-1 bg-[#C9A96E]/15 text-[#1E2D5E] rounded-full text-xs"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

export default function IntakeStep5Review({
  data,
  onEdit,
  onSubmit,
  isSubmitting,
  submitError,
  submitSuccess,
  hasScheduled,
}: Props) {
  if (submitSuccess) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 flex items-center justify-center bg-[#C9A96E]/20 rounded-full mx-auto mb-6">
          <i className="ri-checkbox-circle-line text-[#C9A96E] text-3xl" />
        </div>
        <h3
          className="text-2xl font-light text-[#1E2D5E] mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Application Received!
        </h3>
        <p className="text-sm text-[#1E2D5E]/65 leading-relaxed max-w-md mx-auto">
          Thank you for taking this step, {data.full_name.split(' ')[0]}. Lauren will personally
          review your application and reach out within 24–48 hours to confirm your intake call.
        </p>
        <p className="text-xs text-[#1E2D5E]/45 mt-4">
          A confirmation has been sent to <strong>{data.email}</strong>
        </p>
      </div>
    );
  }

  const readinessLabel = READINESS_OPTIONS.find((r) => r.value === data.readiness)?.label ?? data.readiness;

  return (
    <div>
      <h3
        className="text-xl md:text-2xl font-light text-[#1E2D5E] mb-1"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Review Your Application
      </h3>
      <p className="text-sm text-[#1E2D5E]/60 mb-6 leading-relaxed">
        Take a moment to review everything before submitting.
      </p>

      <SummaryBlock title="Basic Information" stepIndex={1} onEdit={onEdit}>
        <div className="flex items-center gap-2">
          <i className="ri-user-line text-[#C9A96E]" />
          <span>{data.full_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-mail-line text-[#C9A96E]" />
          <span>{data.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-map-pin-line text-[#C9A96E]" />
          <span>{data.location}</span>
        </div>
        {data.phone && (
          <div className="flex items-center gap-2">
            <i className="ri-phone-line text-[#C9A96E]" />
            <span>{data.phone}</span>
          </div>
        )}
      </SummaryBlock>

      <SummaryBlock title="Your Struggles & Impact" stepIndex={2} onEdit={onEdit}>
        <div>
          <p className="text-xs text-[#1E2D5E]/50 mb-1.5">Biggest challenges:</p>
          <TagList items={data.struggles} />
        </div>
        {data.life_impact_areas.length > 0 && (
          <div>
            <p className="text-xs text-[#1E2D5E]/50 mb-1.5 mt-2">Life areas affected:</p>
            <TagList items={data.life_impact_areas} />
          </div>
        )}
        {data.daily_life_description && (
          <div className="mt-2">
            <p className="text-xs text-[#1E2D5E]/50 mb-1">How it affects your daily life:</p>
            <p className="text-[#1E2D5E]/80 leading-relaxed">{data.daily_life_description}</p>
          </div>
        )}
      </SummaryBlock>

      <SummaryBlock title="Goals & Readiness" stepIndex={3} onEdit={onEdit}>
        {data.desired_changes.length > 0 && (
          <div>
            <p className="text-xs text-[#1E2D5E]/50 mb-1.5">What you want to change:</p>
            <TagList items={data.desired_changes} />
          </div>
        )}
        {data.coaching_hope && (
          <div className="mt-2">
            <p className="text-xs text-[#1E2D5E]/50 mb-1">Coaching hope:</p>
            <p className="text-[#1E2D5E]/80 leading-relaxed">{data.coaching_hope}</p>
          </div>
        )}
        {data.readiness && (
          <div className="mt-2">
            <p className="text-xs text-[#1E2D5E]/50 mb-1">Readiness:</p>
            <p>{readinessLabel}</p>
          </div>
        )}
        {data.seek_support_reason && (
          <div className="mt-2">
            <p className="text-xs text-[#1E2D5E]/50 mb-1">Why you&apos;re seeking support now:</p>
            <p className="text-[#1E2D5E]/80 leading-relaxed">{data.seek_support_reason}</p>
          </div>
        )}
      </SummaryBlock>

      <SummaryBlock title="Intake Call" stepIndex={4} onEdit={onEdit}>
        {hasScheduled ? (
          <div className="flex items-center gap-2 text-[#C9A96E]">
            <i className="ri-calendar-check-line" />
            <span className="text-[#1E2D5E]">20-minute intake call time selected</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[#1E2D5E]/50">
            <i className="ri-calendar-line" />
            <span>No time selected yet — you&apos;ll be asked to schedule after approval</span>
          </div>
        )}
      </SummaryBlock>

      {/* Approval Notice */}
      <div className="bg-[#1E2D5E]/5 border border-[#1E2D5E]/10 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-[#C9A96E] text-lg flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#1E2D5E]/75 leading-relaxed">
            <strong className="text-[#1E2D5E]">What happens next:</strong> Lauren will personally
            review your application and reach out within <strong>24–48 hours</strong> via email to
            confirm your intake call and next steps.
          </p>
        </div>
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-xs text-red-600">{submitError}</p>
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={isSubmitting}
        className="w-full py-4 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            Submitting...
          </span>
        ) : (
          'Submit Application'
        )}
      </button>
    </div>
  );
}
